import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from './Toast';
import { X, Loader, CheckCircle2, Search, UploadCloud, ChevronDown, Camera, ScanLine } from 'lucide-react';
import { compressImage, runPool, xhrUpload, playUploadDone } from '../utils/uploadPipeline';
import { lockBodyScroll } from '../utils/scrollLock';

// 上传配置
const PROXY_COMPRESS_PARALLEL = 4;   // 同时压缩几张
const PROXY_UPLOAD_BATCH      = 8;   // 每请求几张
const PROXY_UPLOAD_PARALLEL   = 3;   // 并发请求数

// 进度条子组件
function ProxyProgressBar({ label, pct, active, done: isDone }) {
  const color = isDone ? 'var(--c-success)' : active ? 'var(--c-primary)' : 'var(--c-text-tertiary)';
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
        <span style={{ fontSize: 13, fontWeight: 600, color }}>{label}</span>
        <span style={{ fontSize: 12, color: 'var(--c-text-secondary)' }}>{Math.round(pct)}%</span>
      </div>
      <div style={{ height: 6, background: 'var(--c-bg)', borderRadius: 99, overflow: 'hidden' }}>
        <div style={{
          height: '100%', borderRadius: 99, transition: 'width 0.2s ease',
          width: `${Math.min(100, Math.max(0, pct))}%`,
          background: isDone ? 'var(--c-success)' : 'var(--c-primary)'
        }} />
      </div>
    </div>
  );
}

// 统一代上传弹窗（管理后台首页"上传图片"入口，管理员+代理商共用）
// 规则：
//   - 管理员：可选选择目标（注册用户 / 外部接单人）；不选 → 归属管理员本人
//   - 代理商：必须选择目标（自己名下的成员或外部接单人），不能不选默认归自己——
//     代理商账号是管理多个真实接单人的容器账号，一旦不选目标，这批图默默记到
//     代理商自己名下，识别成功后号码台账会把"首次交付人"记成代理商本人；
//     以后真正的接单人撞到这个号码，重复溯源只能查到"代理商账号"，找不到
//     具体是谁，"查看该用户"也只会跳到代理商自己的图片页（后端在 /upload/any
//     做了同样的校验兜底，防止绕过前端直接调接口）。
//   - 选择了目标 → 归属所选目标（代理只能选自己+名下，后端校验）
// 删除/替换/纠错仍走"图片管理"。
//   - 页面里已经选好对象时（代提代传页）传 targetUser：弹窗里不再重复选择，直接锁定归属该对象；
//     targetUser.self = 管理员选了“本人”，等同于不选（归本人）。不传 targetUser 就是原来的弹窗内选择。
export default function ProxyUploadModal({ onClose, targetUser }) {
  const { user } = useAuth();
  // 代理商账号通常对应多个真实接单人，不能默默把图片归到代理商自己名下——
  // 否则以后号码判重的溯源只能查到"代理商账号"，找不到具体是谁。
  // 管理员保留"不选=归本人"的用法。
  const isAgent = user?.role === 'agent';
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [targets, setTargets] = useState([]);
  const [keyword, setKeyword] = useState('');
  const locked = !!targetUser;
  const lockedId = targetUser && !targetUser.self ? String(targetUser.id) : '';
  const [selectedId, setSelectedId] = useState(lockedId); // '' = 操作者本人
  const [showDropdown, setShowDropdown] = useState(false);
  const [files, setFiles] = useState([]);
  const [previewUrls, setPreviewUrls] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  // 进度状态
  const [phase, setPhase] = useState('select'); // select | compressing | uploading | done
  const [compressPct, setCompressPct] = useState(0);
  const [uploadPct, setUploadPct] = useState(0);
  const [uploadSummary, setUploadSummary] = useState(''); // "X/Y 张"
  const fileRef = useRef(null);
  const dropdownRef = useRef(null);
  const triggerRef = useRef(null);   // 触发按钮 — 用于 getBoundingClientRect
  const listRef = useRef(null);      // portal 列表 — 用于 click-outside 检测
  const [dropdownRect, setDropdownRect] = useState(null);
  const allBlobUrlsRef = useRef([]);
  useEffect(() => () => {
    allBlobUrlsRef.current.forEach(u => URL.revokeObjectURL(u));
  }, []);

  const loadTargets = useCallback(async () => {
    try {
      const [users, ext] = await Promise.all([
        api.get('/users').catch(() => ({ data: { users: [] } })),
        api.get('/users/external').catch(() => ({ data: { users: [] } })),
      ]);
      const merged = [
        ...(users.data.users || []).map(u => ({ id: u.id, name: u.name || u.username || u.phone || `用户${u.id}`, role: u.role, is_external: 0, hasActiveTask: (u.today_active_batches || 0) > 0 })),
        ...(ext.data.users || []).map(u => ({ id: u.id, name: u.name || u.phone || `外部${u.id}`, role: u.role, is_external: 1, hasActiveTask: (u.active_count || 0) > 0 })),
      ];
      const seen = new Set();
      const list = merged.filter(u => { if (seen.has(u.id)) return false; seen.add(u.id); return true; });
      setTargets(list);
    } catch {}
  }, []);
  useEffect(() => { loadTargets(); }, [loadTargets]);

  // previewUrls 的 blob URL 统一由 allBlobUrlsRef 在卸载时 revoke，此处不再逐次 revoke（否则累加时旧预览会失效）

  // 弹窗打开期间锁定底层页面滚动，避免移动端键盘弹出/选择目标时背景页跟着乱滚
  useEffect(() => lockBodyScroll(), []);

  const filtered = useMemo(() => targets.filter(t => {
    if (!keyword.trim()) return true;
    const k = keyword.trim().toLowerCase();
    return String(t.name || '').toLowerCase().includes(k) || String(t.id).includes(k);
  }), [targets, keyword]);
  const selected = locked
    ? (lockedId ? { id: targetUser.id, name: targetUser.name, is_external: targetUser.is_external ? 1 : 0, hasActiveTask: true } : undefined)
    : targets.find(t => String(t.id) === String(selectedId));
  const selfLabel = `不选 · 归属${user?.name || user?.username || '我本人'}（本人）`;

  useEffect(() => {
    if (!showDropdown) return;
    const onDocClick = (e) => {
      const inTrigger = dropdownRef.current && dropdownRef.current.contains(e.target);
      const inList = listRef.current && listRef.current.contains(e.target);
      if (!inTrigger && !inList) setShowDropdown(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [showDropdown]);

  // 打开前先快照触发按钮位置，供 fixed 定位使用
  const handleOpenDropdown = () => {
    if (!showDropdown && triggerRef.current) {
      setDropdownRect(triggerRef.current.getBoundingClientRect());
    }
    setShowDropdown(v => !v);
  };

  const MAX_FILES = 100; // 单次上传图片上限

  const handlePick = () => fileRef.current?.click();
  const handleFiles = (e) => {
    const arr = Array.from(e.target.files || []);
    e.target.value = ''; // 清空 input value，保证同一文件可以再次选中
    if (!arr.length) return;

    // 累加到已选图片，而不是替换——支持多次打开文件选择器追加图片
    setFiles(prev => {
      const combined = [...prev, ...arr].slice(0, MAX_FILES);
      const overflow = prev.length + arr.length > MAX_FILES;
      if (overflow) setError(`已达上限 ${MAX_FILES} 张，多余已截断`);
      else setError('');
      return combined;
    });

    // 同步追加预览 URL（只为本次新增的文件生成，旧的保留）
    const newUrls = arr.map(f => {
      const u = URL.createObjectURL(f);
      allBlobUrlsRef.current.push(u); // 记录，卸载时统一 revoke
      return u;
    });
    setPreviewUrls(prev => [...prev, ...newUrls].slice(0, MAX_FILES));

    setPhase('select');
    setCompressPct(0);
    setUploadPct(0);
  };

  const handleUpload = useCallback(async () => {
    if (!files.length) { setError('请选择图片'); return; }
    // 代理商账号通常对应多个真实接单人，不选目标就默认归到代理商自己名下，
    // 之后号码判重溯源会显示成代理商账号，查不到具体是谁，所以代理商必须选人；
    // 管理员保留"不选=归本人"的用法。
    if (isAgent && !selectedId) {
      setError('请先选择具体的成员或外部接单人，不能不选直接归属到自己名下');
      return;
    }
    setUploading(true);
    setError('');
    const total = files.length;
    const token = localStorage.getItem('token');
    const uploadUrl = selectedId
      ? `/api/images/upload/any?targetUserId=${selectedId}`
      : '/api/images/upload/any';

    try {
      // ── Phase 1: 压缩 ──────────────────────────────────────
      setPhase('compressing');
      setCompressPct(0);
      const compressed = new Array(total);
      let compressDone = 0;
      const compressTasks = files.map((file, idx) => async () => {
        compressed[idx] = await compressImage(file);
        compressDone++;
        setCompressPct((compressDone / total) * 100);
      });
      await runPool(compressTasks, PROXY_COMPRESS_PARALLEL);

      // ── Phase 2: 分批并发上传 ───────────────────────────────
      setPhase('uploading');
      setUploadPct(0);

      // 生成上传批 ID，让多次请求在服务端归为同一批次。
      // 注意：服务端 VALID_BATCH_ID 正则只接受 UUID 或 batch-xxx 格式；
      // 降级回退不能用下划线，否则正则不通过，服务端会为每个并发 XHR 生成不同 UUID，
      // 导致 notify-any-upload-done 找不到批次 → 无通知。
      let cryptoObj = null;
      if (typeof window !== 'undefined' && window.crypto) cryptoObj = window.crypto;
      const uploadBatchId = (cryptoObj && cryptoObj.randomUUID)
        ? cryptoObj.randomUUID()
        : `batch-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

      const batches = [];
      for (let i = 0; i < compressed.length; i += PROXY_UPLOAD_BATCH) {
        batches.push(compressed.slice(i, i + PROXY_UPLOAD_BATCH));
      }

      const totalBytes = compressed.reduce((s, f) => s + f.size, 0);
      const perBatchLoaded = new Array(batches.length).fill(0);
      let uploadedCount = 0;

      const uploadTasks = batches.map((batch, bi) => async () => {
        await xhrUpload({
          url: uploadUrl,
          files: batch,
          token,
          headers: { 'X-Upload-Batch-Id': uploadBatchId },
          timeout: 120000,
          onProgress: (loaded) => {
            perBatchLoaded[bi] = loaded;
            const sumLoaded = perBatchLoaded.reduce((a, b) => a + b, 0);
            setUploadPct(totalBytes > 0 ? (sumLoaded / totalBytes) * 100 : 0);
          }
        });
        uploadedCount += batch.length;
        setUploadSummary(`${uploadedCount}/${total} 张`);
      });

      await runPool(uploadTasks, PROXY_UPLOAD_PARALLEL);

      // 全部上传完成后发一条汇总通知（替代每批各发一条）
      // 注意：必须 await，否则 fetch 的 promise rejection 被静默丢弃
      try {
        await fetch('/api/images/notify-any-upload-done', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'X-Upload-Batch-Id': uploadBatchId,
          },
        });
      } catch (e) {
        console.warn('[ProxyUploadModal] 发送上传通知失败（不影响上传结果）:', e?.message);
      }

      setPhase('done');
      setDone(true);
      // 上传成功后清空已选文件和预览，避免弹窗复用时残留上一批
      setFiles([]);
      setPreviewUrls([]);
      allBlobUrlsRef.current.forEach(u => URL.revokeObjectURL(u));
      allBlobUrlsRef.current = [];
      playUploadDone();
      showToast(
        `已上传 ${total} 张${selected ? `到「${selected.name}」` : '到我的名下'}`,
        'success'
      );
      // 不再自动关闭弹窗——留给用户看到"查看OCR识别记录"入口并点击，
      // 之前1.4秒自动关闭导致这个提示刚出现就被关掉，用户根本看不到
    } catch (err) {
      setPhase('select');
      let msg = '上传失败，请检查网络后重试';
      if (err.status === 403) {
        // 优先用后端返回的具体原因（如工作时间限制），fallback 到权限不足
        try { const body = JSON.parse(err.responseText || '{}'); msg = body.error || '当前无上传权限'; } catch { msg = '当前无上传权限'; }
      } else if (err.status === 413) msg = '图片过大（单张最大 15MB），请压缩后重试';
      else if (err.message === 'timeout') msg = '上传超时，请检查网络信号后重试';
      else if (err.message === 'network') msg = '网络连接失败，请检查网络后重试';
      setError(msg);
    } finally {
      setUploading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files, selectedId, selected, showToast, onClose, user]);

  return createPortal(
    <div style={{
      position: 'fixed', inset: 0, zIndex: 2147483600,
      background: 'rgba(0,0,0,0.55)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20
    }} onClick={uploading ? undefined : onClose}>
      <div style={{
        background: 'var(--c-surface)', borderRadius: 20, width: '100%', maxWidth: 460,
        padding: '18px 16px', boxShadow: '0 12px 40px rgba(0,0,0,0.3)',
        maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden'
      }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexShrink: 0 }}>
          <span style={{ fontSize: 16, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6 }}>
            <UploadCloud size={18} color="var(--c-primary)" /> 上传工作凭证
          </span>
          <button onClick={onClose} disabled={uploading}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
            <X size={18} color="var(--c-text-secondary)" />
          </button>
        </div>

        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', paddingRight: 2 }}>
          {done ? (
            <div style={{ textAlign: 'center', padding: '12px 0 4px' }}>
              <CheckCircle2 size={48} color="var(--c-success)" style={{ margin: '0 auto 10px', display: 'block' }} />
              <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--c-success)', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}><CheckCircle2 size={16} strokeWidth={2} /> 上传完成</div>
              <div style={{ fontSize: 13, color: 'var(--c-text-secondary)', marginBottom: 14 }}>
                已上传到「{selected ? selected.name : (user?.name || '本人')}」名下，后台正在 OCR 识别
              </div>
              {/* 后台 OCR 快捷入口 */}
              <button
                onClick={() => { onClose(); navigate('/admin/ocr'); }}
                style={{
                  width: '100%', height: 46, borderRadius: 12,
                  background: 'rgba(10,132,255,0.08)',
                  border: '1px solid rgba(10,132,255,0.25)',
                  color: 'var(--c-primary)',
                  fontSize: 14, fontWeight: 700, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  marginBottom: 8,
                }}
              >
                <ScanLine size={16} /> 查看 OCR 识别记录
              </button>
              <button onClick={onClose}
                style={{ width: '100%', padding: '10px 0', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: 'var(--c-text-secondary)' }}>
                关闭
              </button>
            </div>
          ) : (
            <>
            {locked ? (
              <div style={{ marginBottom: 12, padding: '10px 12px', background: 'var(--c-bg)', borderRadius: 10, fontSize: 14 }}>
                <span style={{ color: 'var(--c-text-secondary)', fontSize: 13, marginRight: 8 }}>归属</span>
                <strong>{lockedId ? `[${targetUser.is_external ? '外部' : '成员'}] ${targetUser.name}` : `本人（${user?.name || user?.username || '管理员'}）`}</strong>
              </div>
            ) : (
            <>
            {/* 选择归属：代理商必须选具体的人；管理员可以不选，默认归本人 */}
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: isAgent ? 'var(--c-danger)' : 'var(--c-text-secondary)', marginBottom: 6 }}>
                {isAgent ? '归属（必须选择具体的成员或外部接单人）' : '归属（不选则归本人）'}
              </div>
              <div ref={dropdownRef} style={{ position: 'relative' }}>
                <button ref={triggerRef} type="button" onClick={handleOpenDropdown}
                  style={{
                    width: '100%', height: 40, padding: '0 12px',
                    background: 'var(--c-bg)', border: `1px solid ${isAgent && !selected ? 'var(--c-danger)' : 'var(--c-border)'}`, borderRadius: 10,
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    cursor: 'pointer', fontSize: 14, color: 'var(--c-text)'
                  }}>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', paddingRight: 8, color: isAgent && !selected ? 'var(--c-text-tertiary)' : 'var(--c-text)' }}>
                    {selected ? `[${selected.is_external === 1 ? '外部' : '成员'}] ${selected.name}` : (isAgent ? '请选择成员 / 外部接单人' : selfLabel)}
                  </span>
                  <ChevronDown size={16} color="var(--c-text-secondary)" style={{ flexShrink: 0 }} />
                </button>
                {showDropdown && dropdownRect && createPortal(
                  <div
                    ref={listRef}
                    onTouchMove={e => e.stopPropagation()}
                    style={{
                      position: 'fixed',
                      // 向上/向下智能展开：下方空间 < 320px 时改为向上弹出
                      ...(window.innerHeight - dropdownRect.bottom > 320
                        ? { top: dropdownRect.bottom + 4 }
                        : { bottom: window.innerHeight - dropdownRect.top + 4 }),
                      left: dropdownRect.left,
                      width: dropdownRect.width,
                      zIndex: 2147483647,   // 必须高于弹窗遮罩的 2147483600
                      background: 'var(--c-surface)', border: '1px solid var(--c-border)', borderRadius: 10,
                      boxShadow: '0 8px 24px rgba(0,0,0,0.12)', overflow: 'hidden'
                    }}>
                    <div style={{ padding: 8, borderBottom: '1px solid var(--c-divider)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Search size={14} color="var(--c-text-secondary)" />
                        <input className="input" style={{ flex: 1, height: 34 }} placeholder="搜索用户 / 外部接单人"
                          value={keyword} onChange={e => setKeyword(e.target.value)} />
                      </div>
                    </div>
                    {/* 代理商不提供"归本人"这个选项——代理商账号是容器账号，
                        选了也会在提交时被拦下，不如干脆不让选 */}
                    {!isAgent && (
                      <div
                        onClick={() => { setSelectedId(''); setError(''); setShowDropdown(false); }}
                        style={{
                          padding: '10px 12px', cursor: 'pointer', fontSize: 14,
                          background: !selectedId ? 'var(--c-primary-light)' : 'transparent',
                          borderBottom: '1px solid var(--c-divider)'
                        }}>
                        {selfLabel}
                      </div>
                    )}
                    <div style={{ maxHeight: 200, overflowY: 'auto' }}>
                      {filtered.length === 0 ? (
                        <div style={{ padding: '14px 12px', fontSize: 13, color: 'var(--c-text-secondary)', textAlign: 'center' }}>
                          未找到匹配的目标
                        </div>
                      ) : filtered.map(t => (
                        <div key={`${t.is_external}-${t.id}`}
                          onClick={() => { setSelectedId(String(t.id)); setError(''); setShowDropdown(false); }}
                          style={{
                            padding: '9px 12px', cursor: 'pointer', fontSize: 14,
                            background: String(t.id) === selectedId ? 'var(--c-primary-light)' : 'transparent'
                          }}>
                          {t.is_external === 1 ? '[外部] ' : ''}{t.name}
                        </div>
                      ))}
                    </div>
                  </div>,
                  document.body
                )}
              </div>
              {/* 目标当前无进行中任务时给出提示，避免误以为上传会失败 */}
              {selected && !selected.hasActiveTask && (
                <div style={{ fontSize: 12, color: 'var(--c-text-tertiary)', marginTop: 6, lineHeight: 1.5 }}>
                  「{selected.name}」当前没有进行中任务，上传后仍会正常保存，可在「图片管理」中查看。
                </div>
              )}
            </div>
            </>
            )}

            {/* 选图 */}
            <div onClick={handlePick}
              style={{
                border: `2px dashed ${previewUrls.length ? 'var(--c-primary)' : 'var(--c-border)'}`,
                borderRadius: 12, padding: '18px 12px', textAlign: 'center',
                cursor: 'pointer', marginBottom: 10, background: previewUrls.length ? 'var(--c-primary-light)' : 'var(--c-bg)'
              }}>
              <UploadCloud size={28} color="var(--c-primary)" style={{ margin: '0 auto 8px', display: 'block' }} />
              <div style={{ fontSize: 14, fontWeight: 600 }}>{previewUrls.length ? `已选 ${previewUrls.length} 张` : '点击选择图片（可多选）'}</div>
              <div style={{ fontSize: 12, color: 'var(--c-text-tertiary)', marginTop: 4 }}>
                {selected ? `上传后将归属到「${selected.name}」名下` : (isAgent ? '请先在上方选择归属的成员或外部接单人' : '一次上传多张；不选归属则记到本人')}
              </div>
            </div>
            <input ref={fileRef} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={handleFiles} />

            {previewUrls.length > 0 && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6, marginBottom: 10, maxHeight: '22vh', overflowY: 'auto' }}>
                {previewUrls.map((u, i) => (
                  <img key={i} src={u} alt="" style={{ width: '100%', aspectRatio: '1', objectFit: 'cover', borderRadius: 8, border: '1px solid var(--c-border)' }} />
                ))}
              </div>
            )}

            {error && <p style={{ color: 'var(--c-danger)', fontSize: 12, marginBottom: 8 }}>{error}</p>}

            {/* 进度条区域：压缩 + 上传（上传中才显示） */}
            {(phase === 'compressing' || phase === 'uploading') && (
              <div style={{ marginBottom: 10 }}>
                <ProxyProgressBar
                  label="压缩图片"
                  pct={compressPct}
                  active={phase === 'compressing'}
                  done={phase === 'uploading' || phase === 'done'}
                />
                <ProxyProgressBar
                  label={`上传图片${uploadSummary ? `（${uploadSummary}）` : ''}`}
                  pct={uploadPct}
                  active={phase === 'uploading'}
                  done={phase === 'done'}
                />
                <div style={{ fontSize: 12, color: 'var(--c-text-tertiary)', textAlign: 'center' }}>
                  {phase === 'compressing' ? '正在压缩，请稍候...' : `${PROXY_UPLOAD_PARALLEL} 路并发上传中，请保持页面在前台`}
                </div>
              </div>
            )}

            <button className="btn btn-primary" onClick={handleUpload} disabled={uploading || !files.length}
              style={{ width: '100%', height: 46 }}>
              {uploading
                ? <><Loader size={14} className="spin" style={{ verticalAlign: '-2px', marginRight: 4 }} />
                    {phase === 'compressing' ? '压缩中...' : '上传中...'}</>
                : <><Camera size={14} style={{ verticalAlign: '-2px', marginRight: 4 }} />
                    {files.length === 0
                      ? '请先选择图片'
                      : selected
                        ? `上传 ${files.length} 张 → [${selected.is_external === 1 ? '外部' : '成员'}] ${selected.name}`
                        : isAgent
                          ? '请先选择归属的成员 / 外部接单人'
                          : `上传 ${files.length} 张 → 本人`}</>
              }
            </button>
          </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}